import { Box, Button, Dialog, InputLabel, MenuItem, Select, Stack, TextField, Typography } from "@mui/material";
import {
	complianceCertColumns,
	contactPersonColumns,
	contactPersonData,
	factoryAddressDetailColumns,
	factoryBasicDetailColumns,
	fileTableColumns,
	fileTypesData,
	factoryTypeOptions,
	formatDateDisplay,
} from "data/supplierPageData";
import { stateList } from "data/stateMasterList";
import { useEffect, useState } from "react";
import {
	SupplierModalActions,
	SupplierModalHeader,
	modalCancelBtnSx,
	modalContentSx,
	modalFormStack,
	supplierModalPaperProps,
	supplierUi,
} from "../../../shared/supplierUi";
import DisplayTable from "../../CommonComponents/DisplayTable/DisplayTable";
import EditContactPersons from "../../CommonComponents/EditContactPersons/EditContactPersons";
import { getSupplierFileSignedUrl } from "../../CompanyDetails/data";

const COPYABLE_KEYS = [
	"name",
	"factory_type",
	"factory_liscence_no",
	"factory_liscence_issue_date",
	"factory_liscence_expiry_date",
	"factory_gst_no",
	"gmap_location_link",
	"factory_address",
	"factory_city",
	"factory_state",
	"factory_pin_code",
];

const formatDateInput = (value) => formatDateDisplay(value, "YYYY-MM-DD", "");

const withCopyId = (rows = [], prefix) =>
	(rows || []).map((row, index) => ({
		...row,
		_copyId: row?.id != null ? `${prefix}-${row.id}` : `${prefix}-new-${index}-${Date.now()}`,
	}));

const withoutCopyMeta = (rows = []) =>
	(rows || []).map((row) => {
		const { _copyId, id, createdAt, updatedAt, created_at, updated_at, deletedAt, deleted_at, supplier_id, factory_id, ...rest } =
			row || {};
		return rest;
	});

export const buildCopiedFactoryDetails = (factory = {}) => {
	const copied = {};
	COPYABLE_KEYS.forEach((key) => {
		copied[key] = factory?.[key] ?? "";
	});
	const baseName = String(factory?.name || "").trim();
	copied.name = baseName;
	copied.factory_type = "";
	if (copied.factory_pin_code === 0) copied.factory_pin_code = "";
	return copied;
};

const SectionTitle = ({ title, count, action }) => (
	<Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 3, mb: 1.5 }} spacing={1}>
		<Typography sx={{ fontWeight: 600, fontSize: 14, color: supplierUi.textPrimary }}>
			{title}
			{typeof count === "number" ? ` (${count})` : ""}
		</Typography>
		{action}
	</Stack>
);

const CopyFactoryModal = ({ modalOpen, setModalOpen, sourceFactory, submitFunction }) => {
	const [updatedDetails, setUpdatedDetails] = useState({});
	const [files, setFiles] = useState([]);
	const [certs, setCerts] = useState([]);
	const [contacts, setContacts] = useState([]);
	const [pinCodeError, setPinCodeError] = useState("");
	const [typeError, setTypeError] = useState("");

	const [contactModalOpen, setContactModalOpen] = useState(false);
	const [selectedContact, setSelectedContact] = useState();

	const [fileModalOpen, setFileModalOpen] = useState(false);
	const [selectedFile, setSelectedFile] = useState(null);

	const [certModalOpen, setCertModalOpen] = useState(false);
	const [selectedCert, setSelectedCert] = useState(null);

	useEffect(() => {
		if (!modalOpen) return;
		setUpdatedDetails(buildCopiedFactoryDetails(sourceFactory));
		setFiles(withCopyId(sourceFactory?.factoryFiles || [], "file"));
		setCerts(withCopyId(sourceFactory?.factoryComplianceCertificates || [], "cert"));
		setContacts(withCopyId(sourceFactory?.factoryContactPersons || [], "contact"));
		setPinCodeError("");
		setTypeError("");
		setSelectedContact();
		setSelectedFile(null);
		setSelectedCert(null);
	}, [modalOpen, sourceFactory]);

	const handleCloseModal = () => {
		setUpdatedDetails({});
		setFiles([]);
		setCerts([]);
		setContacts([]);
		setPinCodeError("");
		setTypeError("");
		setModalOpen(false);
	};

	const handlePinCodeChange = (event) => {
		const value = event.target.value;
		setUpdatedDetails({ ...updatedDetails, factory_pin_code: value });
		const isValidPinCode = value === "" || /^\d{6}$/.test(value);
		setPinCodeError(isValidPinCode ? "" : "Pincode must be 6 digits");
	};

	const handleFormSubmit = (e) => {
		e.preventDefault();
		const selectedType = String(updatedDetails?.factory_type || "").trim();
		if (!selectedType) {
			setTypeError("Select a factory type");
			return;
		}
		if (pinCodeError) return;
		const body = {};
		[...Object.keys(factoryBasicDetailColumns), ...Object.keys(factoryAddressDetailColumns)].forEach((col) => {
			if (col === "factory_code") return;
			body[col] = updatedDetails[col];
		});
		body.factory_pin_code = updatedDetails.factory_pin_code ? Number(updatedDetails.factory_pin_code) : 0;
		body.files = withoutCopyMeta(files);
		body.compliance_certificates = withoutCopyMeta(certs);
		body.contact_persons = withoutCopyMeta(contacts);
		if (typeof submitFunction === "function") submitFunction(body, setModalOpen);
	};

	const saveContact = async (data) => {
		const nextContact = { ...contactPersonData, ...data };
		delete nextContact.id;
		delete nextContact.supplier_id;
		delete nextContact.factory_id;
		if (selectedContact?._copyId) {
			setContacts((prev) =>
				prev.map((row) =>
					row._copyId === selectedContact._copyId ? { ...row, ...nextContact, _copyId: row._copyId } : row
				)
			);
		} else {
			setContacts((prev) => [...prev, { ...nextContact, _copyId: `contact-new-${Date.now()}` }]);
		}
		return true;
	};

	const saveFile = (e) => {
		e.preventDefault();
		if (!selectedFile?._copyId) return;
		setFiles(files.map((row) => (row._copyId === selectedFile._copyId ? { ...row, ...selectedFile } : row)));
		setFileModalOpen(false);
		setSelectedFile(null);
	};

	const saveCert = (e) => {
		e.preventDefault();
		if (!selectedCert?._copyId) return;
		setCerts(certs.map((row) => (row._copyId === selectedCert._copyId ? { ...row, ...selectedCert } : row)));
		setCertModalOpen(false);
		setSelectedCert(null);
	};

	const sourceLabel = sourceFactory?.name || "selected factory";

	return (
		<>
			<Dialog
				fullWidth
				maxWidth="md"
				open={modalOpen}
				onClose={handleCloseModal}
				component="form"
				onSubmit={handleFormSubmit}
				PaperProps={supplierModalPaperProps}>
				<SupplierModalHeader
					title="Copy factory"
					subtitle={`Review and edit copied details from ${sourceLabel}, then create the factory`}
				/>
				<Box sx={{ ...modalContentSx, pt: 2.5, maxHeight: "70vh", overflowY: "auto" }}>
					<Typography sx={{ fontWeight: 600, fontSize: 14, color: supplierUi.textPrimary, mb: 1.5 }}>
						Basic details
					</Typography>
					<Stack direction="row" width="100%" justifyContent="space-between" flexWrap="wrap" rowGap={2} columnGap={1}>
						<Stack sx={modalFormStack} spacing={0}>
							<InputLabel>Factory Name</InputLabel>
							<TextField
								size="small"
								type="text"
								value={updatedDetails?.name || ""}
								onChange={(e) => setUpdatedDetails({ ...updatedDetails, name: e.target.value })}
								placeholder="Enter Name"
								required
							/>
						</Stack>
						<Stack sx={modalFormStack} spacing={0}>
							<InputLabel>Factory Type</InputLabel>
							<Select
								size="small"
								fullWidth
								displayEmpty
								required
								value={updatedDetails?.factory_type || ""}
								onChange={(e) => {
									setTypeError("");
									setUpdatedDetails({ ...updatedDetails, factory_type: e.target.value });
								}}
								MenuProps={{ PaperProps: { sx: { maxHeight: 240 } } }}>
								<MenuItem value="" disabled>
									Select factory type
								</MenuItem>
								{factoryTypeOptions.map((row) => (
									<MenuItem key={row.key} value={row.key}>
										{row.name}
									</MenuItem>
								))}
							</Select>
							{typeError ? (
								<Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
									{typeError}
								</Typography>
							) : null}
						</Stack>
						<Stack sx={modalFormStack} spacing={0}>
							<InputLabel>Factory Licence Number</InputLabel>
							<TextField
								size="small"
								type="text"
								value={updatedDetails?.factory_liscence_no || ""}
								onChange={(e) => setUpdatedDetails({ ...updatedDetails, factory_liscence_no: e.target.value })}
								placeholder="Enter Licence No"
								required
							/>
						</Stack>
						<Stack sx={modalFormStack} spacing={0}>
							<InputLabel>Licence Issue Date</InputLabel>
							<TextField
								size="small"
								type="date"
								value={formatDateInput(updatedDetails?.factory_liscence_issue_date)}
								onChange={(e) => setUpdatedDetails({ ...updatedDetails, factory_liscence_issue_date: e.target.value })}
								required
							/>
						</Stack>
						<Stack sx={modalFormStack} spacing={0}>
							<InputLabel>Licence Expiry Date</InputLabel>
							<TextField
								size="small"
								type="date"
								value={formatDateInput(updatedDetails?.factory_liscence_expiry_date)}
								onChange={(e) => setUpdatedDetails({ ...updatedDetails, factory_liscence_expiry_date: e.target.value })}
								required
							/>
						</Stack>
						<Stack sx={modalFormStack} spacing={0}>
							<InputLabel>Factory GST No</InputLabel>
							<TextField
								size="small"
								type="text"
								value={updatedDetails?.factory_gst_no || ""}
								onChange={(e) => setUpdatedDetails({ ...updatedDetails, factory_gst_no: e.target.value })}
								placeholder="Enter GST No"
								required
							/>
						</Stack>
						<Stack sx={modalFormStack} spacing={0}>
							<InputLabel>Location Link (Google Maps)</InputLabel>
							<TextField
								size="small"
								type="text"
								value={updatedDetails?.gmap_location_link || ""}
								onChange={(e) => setUpdatedDetails({ ...updatedDetails, gmap_location_link: e.target.value })}
								placeholder="Enter Location Link"
								required
							/>
						</Stack>
					</Stack>

					<SectionTitle title="Address details" />
					<Stack direction="row" width="100%" justifyContent="space-between" flexWrap="wrap" rowGap={2} columnGap={1}>
						<Stack sx={{ width: "100%" }} spacing={0}>
							<InputLabel>Factory Address</InputLabel>
							<TextField
								size="small"
								type="text"
								multiline
								rows={2}
								value={updatedDetails?.factory_address || ""}
								onChange={(e) => setUpdatedDetails({ ...updatedDetails, factory_address: e.target.value })}
								placeholder="Enter Address"
							/>
						</Stack>
						<Stack sx={modalFormStack} spacing={0}>
							<InputLabel>Factory City</InputLabel>
							<TextField
								size="small"
								type="text"
								value={updatedDetails?.factory_city || ""}
								onChange={(e) => setUpdatedDetails({ ...updatedDetails, factory_city: e.target.value })}
								placeholder="Enter city name"
							/>
						</Stack>
						<Stack sx={modalFormStack} spacing={0}>
							<InputLabel>Factory State</InputLabel>
							<Select
								size="small"
								fullWidth
								displayEmpty
								value={updatedDetails?.factory_state || ""}
								onChange={(e) => setUpdatedDetails({ ...updatedDetails, factory_state: e.target.value })}
								MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}>
								<MenuItem value="">Select State</MenuItem>
								{stateList &&
									stateList.map((row) => (
										<MenuItem key={row.state} value={row.state}>
											{row.state}
										</MenuItem>
									))}
							</Select>
						</Stack>
						<Stack sx={modalFormStack} spacing={0}>
							<InputLabel>Pincode</InputLabel>
							<TextField
								size="small"
								type="text"
								value={updatedDetails?.factory_pin_code ?? ""}
								onChange={handlePinCodeChange}
								placeholder="Enter 6 digit pincode"
							/>
							{pinCodeError ? (
								<Typography variant="caption" color="error" mt={0.5}>
									{pinCodeError}
								</Typography>
							) : null}
						</Stack>
					</Stack>

					<SectionTitle title="Files" count={files.length} />
					<Typography variant="caption" sx={{ color: supplierUi.textMuted, display: "block", mb: 1 }}>
						Click a row to edit. Delete removes it from this copy only.
					</Typography>
					<DisplayTable
						rows={files}
						columns={fileTableColumns}
						downloadUrlColumn="url"
						deleteFun={(row) => setFiles(files.filter((item) => item._copyId !== row._copyId))}
						onRowClick={(row) => {
							setSelectedFile(row);
							setFileModalOpen(true);
						}}
						generateFileDownloadUrl={getSupplierFileSignedUrl}
					/>

					<SectionTitle title="Compliance certificates" count={certs.length} />
					<Typography variant="caption" sx={{ color: supplierUi.textMuted, display: "block", mb: 1 }}>
						Click a row to edit issuer and dates. Delete removes it from this copy only.
					</Typography>
					<DisplayTable
						rows={certs}
						columns={complianceCertColumns}
						downloadUrlColumn="url"
						deleteFun={(row) => setCerts(certs.filter((item) => item._copyId !== row._copyId))}
						onRowClick={(row) => {
							setSelectedCert({
								...row,
								compliance_certificate_issue_date: formatDateInput(row.compliance_certificate_issue_date),
								compliance_certificate_exp_date: formatDateInput(row.compliance_certificate_exp_date),
							});
							setCertModalOpen(true);
						}}
						generateFileDownloadUrl={getSupplierFileSignedUrl}
					/>

					<SectionTitle
						title="Contact details"
						count={contacts.length}
						action={
							<Button
								type="button"
								variant="outlined"
								size="small"
								sx={modalCancelBtnSx}
								onClick={() => {
									setSelectedContact();
									setContactModalOpen(true);
								}}>
								Add contact
							</Button>
						}
					/>
					<Typography variant="caption" sx={{ color: supplierUi.textMuted, display: "block", mb: 1 }}>
						Click a row to edit. Delete removes it from this copy only.
					</Typography>
					<DisplayTable
						rows={contacts}
						columns={contactPersonColumns}
						deleteFun={(row) => setContacts(contacts.filter((item) => item._copyId !== row._copyId))}
						onRowClick={(row) => {
							setSelectedContact(row);
							setContactModalOpen(true);
						}}
					/>
				</Box>
				<SupplierModalActions onCancel={handleCloseModal} submitLabel="Create Factory" disableSubmit={!!pinCodeError} />
			</Dialog>

			<EditContactPersons
				modalOpen={contactModalOpen}
				setModalOpen={setContactModalOpen}
				selected={selectedContact}
				setSelected={setSelectedContact}
				submitFunction={saveContact}
				key={contactModalOpen}
			/>

			<Dialog
				fullWidth
				maxWidth="sm"
				open={fileModalOpen}
				onClose={() => {
					setFileModalOpen(false);
					setSelectedFile(null);
				}}
				component="form"
				onSubmit={saveFile}
				PaperProps={supplierModalPaperProps}>
				<SupplierModalHeader title="Edit file" subtitle="Update file name or type for this copy" />
				<Box sx={{ ...modalContentSx, pt: 2.5 }}>
					<Stack spacing={2}>
						<Stack spacing={0}>
							<InputLabel>File name</InputLabel>
							<TextField
								size="small"
								value={selectedFile?.name || ""}
								onChange={(e) => setSelectedFile({ ...selectedFile, name: e.target.value })}
								required
							/>
						</Stack>
						<Stack spacing={0}>
							<InputLabel>File type</InputLabel>
							<Select
								size="small"
								fullWidth
								value={selectedFile?.type || "select"}
								onChange={(e) => setSelectedFile({ ...selectedFile, type: e.target.value })}
								required>
								<MenuItem value="select">Select</MenuItem>
								{fileTypesData?.map((row) => (
									<MenuItem key={row.Key} value={row.Key}>
										{row.name}
									</MenuItem>
								))}
							</Select>
						</Stack>
					</Stack>
				</Box>
				<SupplierModalActions
					onCancel={() => {
						setFileModalOpen(false);
						setSelectedFile(null);
					}}
					submitLabel="Save"
				/>
			</Dialog>

			<Dialog
				fullWidth
				maxWidth="sm"
				open={certModalOpen}
				onClose={() => {
					setCertModalOpen(false);
					setSelectedCert(null);
				}}
				component="form"
				onSubmit={saveCert}
				PaperProps={supplierModalPaperProps}>
				<SupplierModalHeader title="Edit compliance certificate" subtitle="Update issuer and dates for this copy" />
				<Box sx={{ ...modalContentSx, pt: 2.5 }}>
					<Stack spacing={2}>
						<Stack spacing={0}>
							<InputLabel>Issuer</InputLabel>
							<TextField
								size="small"
								value={selectedCert?.compliance_certificate_issuer || ""}
								onChange={(e) =>
									setSelectedCert({ ...selectedCert, compliance_certificate_issuer: e.target.value })
								}
								required
							/>
						</Stack>
						<Stack spacing={0}>
							<InputLabel>Issue date</InputLabel>
							<TextField
								size="small"
								type="date"
								value={formatDateInput(selectedCert?.compliance_certificate_issue_date)}
								onChange={(e) =>
									setSelectedCert({ ...selectedCert, compliance_certificate_issue_date: e.target.value })
								}
								required
							/>
						</Stack>
						<Stack spacing={0}>
							<InputLabel>Expiry date</InputLabel>
							<TextField
								size="small"
								type="date"
								value={formatDateInput(selectedCert?.compliance_certificate_exp_date)}
								onChange={(e) =>
									setSelectedCert({ ...selectedCert, compliance_certificate_exp_date: e.target.value })
								}
								required
							/>
						</Stack>
					</Stack>
				</Box>
				<SupplierModalActions
					onCancel={() => {
						setCertModalOpen(false);
						setSelectedCert(null);
					}}
					submitLabel="Save"
				/>
			</Dialog>
		</>
	);
};

export default CopyFactoryModal;
